import React, { useCallback, useMemo, useRef } from 'react';
import { Formik } from 'formik';
import PropTypes from 'prop-types';

import { useAppContext } from '../hooks';

function Form({
  initialValues,
  validate,
  transform,
  onSubmit,
  children,
  ...rest
}) {
  const { config } = useAppContext();
  const serverErrors = useRef({ ...config.form_errors });
  const initialTouched = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(config.form_errors).map(([name]) => [name, true])
      ),
    [config.form_errors]
  );
  const mergedInitialValues = useMemo(
    () => ({
      ...initialValues,
      ...config.form_values,
    }),
    [config.form_values, initialValues]
  );
  const handleSubmit = useCallback(
    (values) => {
      if (typeof onSubmit === 'function') {
        onSubmit(values);
      }
      if (typeof transform === 'function') {
        values = transform(values);
      }
      // until we have proper apis, submit form data the old fashioned style...
      const form = document.createElement('form');
      form.method = 'post';
      for (const key in values) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        if (typeof values[key] === 'object') {
          input.value = JSON.stringify(values[key]);
        } else if (values[key] === undefined || values[key] === null) {
          input.value = '';
        } else {
          input.value = values[key];
        }
        form.appendChild(input);
      }
      document.body.appendChild(form);
      form.submit();
    },
    [onSubmit, transform]
  );
  return (
    <Formik
      initialValues={mergedInitialValues}
      initialTouched={initialTouched}
      validate={(values) => {
        const errors = {
          ...validate(values),
          ...serverErrors.current,
        };
        // only show server errors on first validation
        if (serverErrors.current) {
          serverErrors.current = null;
        }
        return errors;
      }}
      validateOnMount
      onSubmit={handleSubmit}
      {...rest}
    >
      {(formikProps) => (
        <form data-testid="form" onSubmit={formikProps.handleSubmit}>
          {children}
        </form>
      )}
    </Formik>
  );
}

Form.propTypes = {
  ...Formik.propTypes,
  children: PropTypes.node,
  transform: PropTypes.func,
};

export default Form;
